import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface WeatherData {
  currently: {
    temperature: number;
    apparentTemperature: number;
    summary: string;
    icon: string;
    humidity: number;
    windSpeed: number;
    precipProbability: number;
  };
  daily: {
    data: Array<{
      time: number;
      temperatureHigh: number;
      temperatureLow: number;
      summary: string;
      icon: string;
      precipProbability: number;
    }>;
  };
}

export interface ProcessedWeatherData {
  current: {
    temperature: number;
    condition: string;
    icon: string;
    tempHigh: number;
    tempLow: number;
    location: string;
  };
  forecast: Array<{
    day: string;
    icon: string;
    tempHigh: number;
    tempLow: number;
  }>;
}

@Injectable({
  providedIn: 'root'
})
export class WeatherService {
  private readonly weatherCacheUrl = environment.weatherCacheUrl;

  constructor(private http: HttpClient) {}

  /**
   * Get weather data for given coordinates
   */
  getWeather(latitude: number, longitude: number, location?: string): Observable<ProcessedWeatherData | null> {
    const url = `${this.weatherCacheUrl}/forecast/cache/${latitude},${longitude}?units=us`;
    // console.log('[WeatherService] Fetching weather from:', url);
    // console.log('[WeatherService] Coordinates:', { latitude, longitude, location });

    return this.http.get<WeatherData>(url).pipe(
      map(data => {
        // console.log('[WeatherService] Weather data received:', data);
        const processed = this.processWeatherData(data, location);
        // console.log('[WeatherService] Processed weather data:', processed);
        return processed;
      }),
      catchError(error => {
        console.error('[WeatherService] Error fetching weather data:', error);
        console.error('[WeatherService] Error status:', error.status);
        console.error('[WeatherService] Error message:', error.message);
        console.error('[WeatherService] Full error:', JSON.stringify(error, null, 2));
        return of(null);
      })
    );
  }

  /**
   * Geocode an address to get coordinates using a free geocoding service
   */
  geocodeAddress(address: string): Observable<{ latitude: number; longitude: number } | null> {
    // console.log('[WeatherService] Geocoding address:', address);

    // Using Nominatim (OpenStreetMap) free geocoding API
    const encodedAddress = encodeURIComponent(address);
    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodedAddress}&limit=1`;
    // console.log('[WeatherService] Geocoding URL:', url);

    return this.http.get<any[]>(url, {
      headers: {
        'User-Agent': 'VesselApp/1.0' // Nominatim requires a User-Agent
      }
    }).pipe(
      map(results => {
        // console.log('[WeatherService] Geocoding results:', results);
        if (results && results.length > 0) {
          const coords = {
            latitude: parseFloat(results[0].lat),
            longitude: parseFloat(results[0].lon)
          };
          // console.log('[WeatherService] Geocoded coordinates:', coords);
          return coords;
        }
        console.warn('[WeatherService] No geocoding results found for address:', address);
        return null;
      }),
      catchError(error => {
        console.error('[WeatherService] Error geocoding address:', error);
        console.error('[WeatherService] Geocoding error status:', error.status);
        console.error('[WeatherService] Geocoding error message:', error.message);
        return of(null);
      })
    );
  }

  private processWeatherData(data: WeatherData, location?: string): ProcessedWeatherData {
    const currently = data.currently;
    const daily = data.daily.data;

    // Get today's high/low from daily data
    const today = daily[0];

    return {
      current: {
        temperature: Math.round(currently.temperature),
        condition: this.formatCondition(currently.summary),
        icon: this.mapIcon(currently.icon),
        tempHigh: Math.round(today.temperatureHigh),
        tempLow: Math.round(today.temperatureLow),
        location: location || 'Current Location'
      },
      forecast: daily.slice(1, 6).map((day, index) => ({
        day: this.getDayName(day.time, index + 1),
        icon: this.mapIcon(day.icon),
        tempHigh: Math.round(day.temperatureHigh),
        tempLow: Math.round(day.temperatureLow)
      }))
    };
  }

  private formatCondition(summary: string): string {
    // Clean up the summary text
    return summary || 'Unknown';
  }

  private getDayName(timestamp: number, dayOffset: number): string {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const date = new Date(timestamp * 1000);
    return days[date.getDay()];
  }

  /**
   * Map Pirate Weather icon names to Ionic icon names
   */
  private mapIcon(pirateIcon: string): string {
    const iconMap: { [key: string]: string } = {
      'clear-day': 'sunny',
      'clear-night': 'moon',
      'rain': 'rainy',
      'snow': 'snow',
      'sleet': 'snow',
      'wind': 'cloudy',
      'fog': 'cloudy',
      'cloudy': 'cloudy',
      'partly-cloudy-day': 'partly-sunny',
      'partly-cloudy-night': 'cloudy-night',
      'thunderstorm': 'thunderstorm'
    };

    return iconMap[pirateIcon] || 'cloudy';
  }
}
